import { i18n } from '@/i18n/i18n'
import store from '@/store'

/*
  google palette

  HEX: #4285F4   blu
  HEX: #FBBC05   yellow
  HEX: #34A853   green
  HEX: #EA4335   red

  message object structure
  {
    color: <hex code>,
    text: <msg text>
  }
*/
export default {
  namespaced: true,
  state: {
    messages: [],
    show: false,
    userConfirm: null,
    pushMessages: [],
    pushMessagesShow: false,
    pushMessagesPanelSize: 30
  },
  getters: {
    messages (state) {
      return state.messages
    },
    pushMessages (state) {
      state.pushMessages = getStateLocalStorage(store, state).pushMessages
      return state.pushMessages
    },
    pushMessagesShow (state) {
      return state.pushMessagesShow
    },
    pushMessagesPanelSize (state) {
      return state.pushMessagesPanelSize
    },
    show (state) {
      return state.show
    },
    userConfirm (state) {
      return state.userConfirm
    }
  },
  mutations: {
    addMessage (state, msgObject) {
      state.messages.unshift(Object.assign({ id: Date.now() }, msgObject))
      state.show = true
    },
    removeMessage (state, msgObject) {
      state.show = false
    },
    addPushMessage (state, msgObject) {
      state.pushMessages.push(msgObject)
      setStateLocalStorage(store, state)
    },
    removePushMessage (state, msgObject) {
      state.pushMessages = state.pushMessages.filter((msg) => msg.receptionTime !== msgObject.receptionTime)
      setStateLocalStorage(store, state)
    },
    removeAllPushMessage (state) {
      state.pushMessages = []
      setStateLocalStorage(store, state)
    },
    togglePushMessages (state) {
      state.pushMessagesShow = !state.pushMessagesShow
    },
    setPushMessagesPanelSize (state, panelSize) {
      state.pushMessagesPanelSize = panelSize
    },
    userConfirm (state, userConfirm) {
      state.userConfirm = userConfirm
    }
  },
  actions: {

    async notifyError ({ commit }, error) {
      let msgText = i18n.t('message.errorPrefix')
      let msgTextDetails = ''

      // /////////////////////////////////////////////////////////////////
      // #16341 fixing error cast to JSON in case of Axios blob request
      if (
        error?.request?.responseType === 'blob' &&
        error?.response?.data instanceof Blob &&
        error.response.data?.type &&
        error.response.data.type.toLowerCase().indexOf('json') !== -1
      ) {
        error.response.data = JSON.parse(await error.response.data.text())
      }
      // /////////////////////////////////////////////////////////////////

      // main message
      if (error?.response?.data?.error?.exceptionDetails) {
        msgText += '\n\n' + error.response.data.error.exceptionDetails
      } else if (error.message) {
        msgText += '\n\n' + error.message
      }
      // endpoint
      if (error?.response?.data?.error?.path) {
        if (error.config) {
          msgTextDetails += error.config.baseURL
        }
        msgTextDetails += error?.response?.data?.error?.path + '\n\n'
      }
      // details
      if (error.response && error.response.data && error.response.data.error) {
        if (error.response.data.error.exceptionDetails) {
          msgTextDetails += JSON.stringify(error.response.data, null, ' ')
        } else if (error.response.data.error.message) {
          msgTextDetails += error.response.data.error.message
        }
      } else if (error.stack) {
        msgTextDetails += error.stack
      }
      commit('addMessage', { color: '#EA4335', text: msgText, details: msgTextDetails })
    },

    notifyMessage ({ commit }, msgText) {
      commit('addMessage', { color: '#34A853', text: msgText })
    },

    notifyWarning ({ commit }, {
      message,
      hideClose = false
    } = {}) {
      commit('addMessage', { color: '#FBBC05', text: message, hideClose })
    },

    removeMessage ({ commit }, msgObject) {
      commit('removeMessage', msgObject)
    },

    /** Return a promire resolved with the user feedback (true/false) */
    notifyUserConfirm ({ commit }, {
      title,
      messages,
      confirm = 'label.confirm',
      cancel = 'label.cancel',
      data = {},
      form = null,
      hideCancel = false,
      width = null
    } = {}) {
      return new Promise((resolve) => {
        commit('userConfirm', {
          title,
          messages,
          data,
          form,
          confirm,
          cancel,
          hideCancel,
          width,
          onConfirm: (payload) => resolve(Object.assign(data, payload) || true),
          onCancel: () => resolve(false)
        })
      })
    },

    removeUserConfirm ({ commit }) {
      commit('userConfirm', null)
    },

    addPushMessage ({ commit }, msgObject) {
      commit('addPushMessage', msgObject)
    },

    removePushMessage ({ commit }, msgObject) {
      commit('removePushMessage', msgObject)
    },

    removeAllPushMessage ({ commit }) {
      commit('removeAllPushMessage')
    },

    togglePushMessages ({ commit }) {
      commit('togglePushMessages')
    },

    setPushMessagesPanelSize ({ commit }, size) {
      commit('setPushMessagesPanelSize', size)
    }
  }
}

function getStateLocalStorage (store, state) {
  const userId = store.getters['userDataStore/userId']
  let localStorageItem = localStorage.getItem(`Agilis-notificationStore-${userId}`)
  if (!localStorageItem || localStorageItem === 'undefined') {
    // initialize local storage
    localStorageItem = setStateLocalStorage(store, state)
  }
  return JSON.parse(localStorageItem)
}

function setStateLocalStorage (store, state) {
  const userId = store.getters['userDataStore/userId']
  const localStorageItem = JSON.stringify(state)
  localStorage.setItem(`Agilis-notificationStore-${userId}`, localStorageItem)
  return localStorageItem
}
